import type { ComputedRef, InjectionKey, Ref } from "vue";
import { getCurrentInstance, inject, nextTick } from "vue";

import { performanceLog } from "../log";
import type { DeepReadonly, DeepReadonlyExceptArrays } from "../type";

export function nextTicks(ticks: number, callback: () => void) {
  const start = performance.now();
  if (ticks === 0) {
    callback();
  } else {
    void nextTick(() => {
      performanceLog(`tick ${ticks}`, start);
      nextTicks(ticks - 1, callback);
    });
  }
}

/**
 * Injects a value from the Vue composition API's injection system.
 * If the value is not found, an error is thrown.
 */
export function injectStrict<T>(key: InjectionKey<T>, fallback?: T) {
  const instance = getCurrentInstance();
  const resolved = inject(key, fallback);
  if (!resolved) {
    // outside setup and render there is no instance to resolve against, e.g. inside a computed
    throw Error(
      instance
        ? `Could not find "${key.description}" in the context of <${instance.type.__name ?? instance.type.name}>.`
        : `Cannot inject "${key.description}" outside of setup or render.`,
    );
  }
  return resolved;
}

// Returns an unmodifiable view of ref.
// This is type level only and can be reverted to modifiable for tests.
export function asReadonly<T>(ref: Ref<T>) {
  return ref as unknown as ComputedRef<DeepReadonly<T>>;
}

// Returns an unmodifiable view of ref, except arrays.
// use this instead of asReadonly if readonly arrays cause too much trouble.
export function asSemiReadonly<T>(ref: Ref<T>) {
  return ref as unknown as ComputedRef<DeepReadonlyExceptArrays<T>>;
}
